import type { Appearance } from "@stripe/stripe-js";
import { useTheme } from "next-themes";
import { useMemo } from "react";

/**
 * Custom hook to generate Stripe Elements appearance configuration based on the current theme.
 *
 * This ensures that the Stripe payment form matches the look and feel of the rest of the application,
 * including support for light and dark themes.
 */
export const useStripeAppearance = (): Appearance => {
  const { resolvedTheme } = useTheme();

  return useMemo<Appearance>(() => {
    const isDark = resolvedTheme === "dark";

    return {
      theme: isDark ? "night" : "stripe",
      variables: {
        colorPrimary: resolveCssVar("--text-primary"),
        colorBackground: resolveCssVar("--bg-surface-elevate-low"),
        colorText: resolveCssVar("--text-dark"),
        colorDanger: resolveCssVar("--text-negative"),
        fontFamily: "Inter, system-ui, sans-serif",
        spacingUnit: "4px",
        borderRadius: "8px",
      },
    };
  }, [resolvedTheme]);
};

const rgbaToHex = (rgba: string): string => {
  const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) return rgba;
  const [, r, g, b] = match;
  return `#${[r, g, b].map((c) => Number(c).toString(16).padStart(2, "0")).join("")}`;
};

const resolveCssVar = (name: string): string => {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return rgbaToHex(value);
};
